import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	FiAlertTriangle, FiCheck, FiClock, FiDatabase, FiDownload, FiFile, FiGrid, FiRefreshCw, FiShield, FiUploadCloud
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "../../Auth/PermissionsContext";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import { usePageHeader } from "../../hooks/usePageHeader";
import { MacroDataState, fetchMacroData } from "../../Redux/Slice/MacroDataSlice";
import { AnomalyBanner } from "./AnomalyBanner";
import { CatalogTable } from "./CatalogTable";
import { DataRemovalConfirm } from "./DataRemovalConfirm";
import { EstimatedProgressBar } from "./EstimatedProgressBar";
import MacroPublishAfterSaveButton from "./MacroPublishAfterSaveButton";
import { PublishProductionCard } from "./PublishProductionCard";
import { StructureWarnings } from "./StructureWarnings";
import { MacroImportHistoryItem, MacroImportReport, macroDataClient } from "./macroDataClient";
import { showSuccessModal } from "../../Helper/showSuccessModal";

/** Un retrait de données publiées en attente de confirmation. */
type PendingRemoval = { items: string[]; fingerprint: string; listChanged: boolean };

const number = (value: number): string => new Intl.NumberFormat("fr-FR").format(value);
const shortDate = (value?: string | null): string =>
	value ? new Date(value).toLocaleDateString("fr-BE") : "-";

/** Import-only administration page for the authoritative macroeconomic workbook. */
const MacroData: React.FC = () => {
	usePageHeader({ title: "Données macroéconomiques" });
	const { canFeatureWrite } = usePermissions();
	const canWrite = canFeatureWrite("macro-data");
	const canPublishProd = canFeatureWrite("macro-publish");
	const dispatch = useDispatch();
	const [file, setFile] = useState<File | null>(null);
	const [report, setReport] = useState<MacroImportReport | null>(null);
	const [working, setWorking] = useState<"validate" | "commit" | "publish" | null>(null);
	const [downloading, setDownloading] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	/** Refus 409 « ce classeur retire des données » : la liste du SERVEUR, son empreinte, et si
	 *  elle a changé depuis la validation. Prioritaire sur la liste du rapport de validation. */
	const [dataLoss, setDataLoss] = useState<PendingRemoval | null>(null);
	/** Clé de la liste de retraits que l'opérateur a acceptée (case cochée). La case n'est cochée
	 *  QUE si cette clé est celle de la liste affichée : une nouvelle liste la décoche dans le même
	 *  rendu, sans fenêtre où l'ancienne acceptation vaudrait pour la nouvelle liste. */
	const [acceptedRemovalKey, setAcceptedRemovalKey] = useState<string | null>(null);
	const removalRef = useRef<HTMLElement>(null);
	/** Compteur de rafraîchissement du bandeau d'anomalies : il suit les mêmes gestes que le reste
	 *  de l'écran, sans jamais être recalculé par le simple fait d'être affiché. */
	const [anomalyKey, setAnomalyKey] = useState(0);

	// Cache partagé (même patron que Catégories / Types de contenu) : chargé une fois, il survit
	// à la navigation. Revenir sur l'écran n'entraîne plus deux appels réseau.
	const { imports: history, catalog, loading, isRefreshing, error: storeError, initialized } = useSelector(
		(state: { macroData: MacroDataState }) => state.macroData
	);

	useEffect(() => {
		if (!initialized && !loading && !isRefreshing) {
			dispatch(fetchMacroData() as any);
		}
	}, [dispatch, initialized, loading, isRefreshing]);

	/** Rechargement explicite : les données restent visibles, seul le bouton tourne. */
	const load = useCallback(async () => {
		await (dispatch(fetchMacroData(true) as any) as unknown as Promise<void>);
		setAnomalyKey(value => value + 1);
	}, [dispatch]);

	const latestCommitted = useMemo(
		() => history.find(item => item.status === "committed" || item.status === "committed-publish-failed"),
		[history]
	);
	/** Période la plus récente réellement publiée, tous sujets confondus. */
	const latestPeriod = useMemo(() => {
		const periods = (catalog?.datasets ?? []).map(item => item.lastPeriod).filter(Boolean) as string[];
		return periods.length > 0 ? periods.sort().slice(-1)[0] : null;
	}, [catalog]);

	/**
	 * La modale qui suit un import reussi : elle rappelle que la production reste sur les valeurs
	 * precedentes et propose la promotion, mais SEULEMENT a qui detient la permission macro-publish
	 * (le bouton ne se rend pas sans elle). Pour les autres, c'est une simple confirmation.
	 */
	const proposerLaProduction = (title: string, message: string) => {
		showSuccessModal({
			title,
			message,
			note: canPublishProd
				? "La production affiche encore les valeurs précédentes tant qu'elles ne sont pas promues."
				: "La mise en production reste à faire par une personne qui en a le droit.",
			confirmLabel: "Plus tard",
			renderPublish: close => (
				<MacroPublishAfterSaveButton
					onPublished={() => {
						close();
						void load();
					}}
				/>
			)
		});
	};

	/**
	 * Le retrait en attente de confirmation : celui que le serveur vient d'opposer, sinon celui
	 * que la validation annonce. Tant qu'il existe, le bouton de confirmation devient « Confirmer
	 * le retrait de ces données » et attend la case.
	 */
	const pendingRemoval = useMemo<PendingRemoval | null>(() => {
		if (dataLoss && dataLoss.items.length > 0) return dataLoss;
		const items = report?.periodRegressions ?? [];
		if (report?.isValid && !report.alreadyCommitted && items.length > 0) {
			return { items, fingerprint: report.periodRegressionsFingerprint ?? "", listChanged: false };
		}
		return null;
	}, [dataLoss, report]);
	const pendingRemovalKey = pendingRemoval ? `${pendingRemoval.fingerprint}|${pendingRemoval.items.join("\n")}` : "";
	const removalAccepted = pendingRemoval != null && acceptedRemovalKey === pendingRemovalKey;
	// Sans empreinte, la confirmation porterait sur une liste que le serveur n'a pas garantie :
	// l'écran ne l'envoie jamais.
	const removalConfirmable = pendingRemoval != null && pendingRemoval.fingerprint.length > 0;

	const validate = async () => {
		if (!file || !canWrite) return;
		setWorking("validate");
		setError(null);
		setDataLoss(null);
		try {
			const result = await macroDataClient.validate(file);
			setReport(result);
			toast[result.isValid ? "success" : "warning"](
				result.isValid ? "Classeur validé. Vérifiez le rapport avant de confirmer." : "Le classeur contient des erreurs bloquantes."
			);
			await load();
		} catch (err: any) {
			setError(err?.response?.data?.message ?? err?.response?.data ?? "La validation du classeur a échoué.");
		} finally {
			setWorking(null);
		}
	};

	const commit = async (allowDataLoss = false, acceptedLoss?: string) => {
		if (!report?.isValid || report.alreadyCommitted || !canWrite) return;
		setWorking("commit");
		setError(null);
		try {
			const result = await macroDataClient.commit(report.batchId, allowDataLoss, acceptedLoss);
			toast.success(
				`${allowDataLoss ? "Retrait confirmé. " : ""}${number(result.observationCount)} observations remplacées; aperçu ${result.previewPublished ? "publié" : "à republier"}.`
			);
			setDataLoss(null);
			setReport(current => (current ? { ...current, alreadyCommitted: true } : current));
			await load();
			// L'apercu est a jour, la production ne l'est pas : on propose le geste suivant a qui
			// detient le droit de le faire, plutot que de le laisser deviner qu'il reste une etape.
			if (result.previewPublished) {
				proposerLaProduction(
					"Aperçu publié",
					`${number(result.observationCount)} observations remplacées. L'aperçu est à jour.`
				);
			}
		} catch (err: any) {
			const payload = err?.response?.data;
			if (payload?.dataLoss) {
				// Refus volontaire : la liste EXACTE de ce qui serait retiré s'affiche à côté du bouton,
				// et la confirmation portera sur cette liste-là (son empreinte).
				const listChanged = Boolean(payload.listChanged);
				setDataLoss({
					items: Array.isArray(payload.regressions) ? payload.regressions : [],
					fingerprint: typeof payload.fingerprint === "string" ? payload.fingerprint : "",
					listChanged
				});
				setError(null);
				toast.warn(
					listChanged
						? "La liste des données retirées a changé : relisez-la avant de confirmer."
						: "Ce classeur retire des données déjà publiées : relisez la liste puis confirmez le retrait."
				);
				// Le refus arrive là où l'opérateur regarde : l'encadré est remis dans son champ de vision.
				window.setTimeout(() => {
					const node = removalRef.current;
					if (node && typeof node.scrollIntoView === "function") {
						node.scrollIntoView({ behavior: "smooth", block: "center" });
					}
				}, 0);
			} else {
				setError(payload?.message ?? payload ?? "La confirmation de l'import a échoué.");
			}
		} finally {
			setWorking(null);
		}
	};

	const republish = async () => {
		if (!canWrite) return;
		setWorking("publish");
		setError(null);
		try {
			const published = await macroDataClient.republishPreview();
			toast[published ? "success" : "warning"](published ? "Artefacts d'aperçu republiés." : "Aucun import confirmé à publier.");
			await load();
			if (published) {
				proposerLaProduction("Aperçu republié", "Les artefacts d'aperçu ont été régénérés.");
			}
		} catch (err: any) {
			setError(err?.response?.data?.message ?? "La republication a échoué.");
		} finally {
			setWorking(null);
		}
	};

	const download = async (item: MacroImportHistoryItem) => {
		setDownloading(item.id);
		try {
			await macroDataClient.downloadWorkbook(item.id, item.fileName);
		} catch {
			toast.error("Classeur indisponible : il a été purgé par la rétention.");
		} finally {
			setDownloading(null);
		}
	};

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				{/* En-tête au format des autres pages de l'admin (Sitemaps, Backups) : carte blanche,
				    pastille d'icône, titre + sous-titre, action à droite. */}
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
						<FiDatabase className="h-5 w-5" />
					</span>
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold text-slate-900">Données macroéconomiques</h1>
						<p className="text-sm text-slate-500">
							Le classeur officiel est la source de vérité du portail des données : validez, confirmez, puis publiez en production.
						</p>
					</div>
					<button
						type="button"
						onClick={load}
						disabled={loading || isRefreshing}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						<FiRefreshCw className={`h-4 w-4 ${loading || isRefreshing ? "animate-spin" : ""}`} /> Actualiser
					</button>
				</div>

				{/* État DURABLE de la base, avant toute autre information : ce bandeau ne parle pas du
				    fichier qu'on vient de déposer mais de ce qui reste à corriger, et il reste tant que
				    la correction n'a pas eu lieu. */}
				<AnomalyBanner canWrite={canWrite} refreshKey={anomalyKey} />

				{/* Bandeau de source : fichier actif, date de la base et période la plus récente publiée. */}
				<div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
					{[
						{ label: "Classeur actif", value: loading ? "Chargement…" : latestCommitted?.fileName ?? "Aucun classeur confirmé", hint: latestCommitted?.committedAtUtc ? `Confirmé le ${new Date(latestCommitted.committedAtUtc).toLocaleString("fr-BE")}` : null },
						{ label: "Base arrêtée au", value: shortDate(catalog?.activeBatch?.sourceAsOfUtc), hint: "Date « Sommaire » du classeur" },
						{ label: "Donnée la plus récente", value: latestPeriod ?? "-", hint: "Période la plus avancée, tous sujets confondus" },
						{ label: "Empreinte SHA-256", value: latestCommitted?.sha256 ? `${latestCommitted.sha256.slice(0, 12)}…` : "-", hint: "Preuve d'intégrité du fichier source", mono: true },
					].map(tile => (
						<div key={tile.label} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tile.label}</p>
							<p className={`mt-1 truncate text-lg font-bold text-slate-900 ${tile.mono ? "font-mono text-base" : ""}`} title={String(tile.value)}>{tile.value}</p>
							{tile.hint && <p className="mt-0.5 text-xs text-slate-400">{tile.hint}</p>}
						</div>
					))}
				</div>

				{(error ?? storeError) && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error ?? storeError}</div>}

				{latestCommitted?.status === "committed-publish-failed" && (
					<div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">
						La dernière confirmation a été enregistrée mais sa projection d'aperçu a échoué
						{latestCommitted.report?.fileName ? ` (${latestCommitted.report.fileName})` : ""}. Utilisez « Republier l'aperçu ».
					</div>
				)}

				{/* 1. Production : l'action de plus haut niveau reste en tête de page. */}
				<PublishProductionCard
					production={catalog?.production ?? { upToDate: true, staleRecordIds: [], lastPromotedAtUtc: null }}
					canPublish={canPublishProd}
					onPublished={load}
				/>

				{report && (
					<section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="flex flex-wrap items-start justify-between gap-4">
							<div><p className="text-xs font-bold uppercase tracking-widest text-blue-700">Rapport de validation</p><h2 className="mt-1 text-xl font-bold text-slate-900">{report.fileName}</h2><p className="mt-1 font-mono text-xs text-slate-400">SHA-256 {report.sha256}</p></div>
							<span className={`rounded-full px-3 py-1 text-xs font-bold ${report.isValid ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{report.isValid ? "VALIDE" : "BLOQUÉ"}</span>
						</div>
						<div className="mt-5 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
							{[["Sujets", report.datasetCount], ["Séries", report.seriesCount], ["Périodes", report.periodCount], ["Observations", report.observationCount], ["Lacunes préservées", report.missingCellCount]].map(([label, value]) => <div key={String(label)} className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold uppercase text-slate-500">{label}</p><p className="mt-1 text-xl font-bold text-slate-900">{number(Number(value))}</p></div>)}
						</div>
						<StructureWarnings report={report} />
						{[...report.errors, ...report.warnings].map(message => <p key={message} className={`mt-3 rounded-lg px-3 py-2 text-sm ${report.errors.includes(message) ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{message}</p>)}
						<div className="mt-5 overflow-x-auto"><table className="min-w-full text-left text-sm"><thead><tr className="border-b border-slate-200 text-xs uppercase text-slate-500"><th className="px-3 py-3">Sujet</th><th className="px-3 py-3">Séries</th><th className="px-3 py-3">Périodes</th><th className="px-3 py-3">Observations</th><th className="px-3 py-3">Plage</th></tr></thead><tbody>{report.datasets.map(dataset => <tr key={dataset.id} className="border-b border-slate-100"><td className="px-3 py-3 font-semibold text-slate-800">{dataset.name}</td><td className="px-3 py-3">{number(dataset.seriesCount)}</td><td className="px-3 py-3">{number(dataset.periodCount)}</td><td className="px-3 py-3">{number(dataset.observationCount)}</td><td className="whitespace-nowrap px-3 py-3 text-slate-500">{dataset.firstPeriod} → {dataset.lastPeriod}</td></tr>)}</tbody></table></div>
					</section>
				)}

				{/* 2. Téléversement : les deux gestes quotidiens (charger puis confirmer) passent
				    AVANT l'état du portail, qui est un compte rendu : on agit d'abord, on
				    constate ensuite, sans traverser le catalogue à chaque import. */}
				<section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="flex items-start gap-3">
							<div className="rounded-xl bg-blue-50 p-3 text-blue-700"><FiUploadCloud className="h-5 w-5" /></div>
							<div>
								<h2 className="text-lg font-bold text-slate-900">1. Charger et valider</h2>
								<p className="mt-1 text-sm text-slate-500">Format accepté : .xlsx · limite 50 Mo · le fichier est conservé avec son empreinte SHA-256.</p>
							</div>
						</div>
						<label className="mt-5 flex cursor-pointer items-center gap-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 hover:border-blue-300">
							<FiFile className="h-7 w-7 text-blue-600" />
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-semibold text-slate-800">{file?.name ?? "Sélectionner le classeur officiel"}</p>
								<p className="text-xs text-slate-500">{file ? `${(file.size / 1024 / 1024).toFixed(2)} Mo` : "Les cellules vides resteront des lacunes statistiques."}</p>
							</div>
							<input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="sr-only" onChange={event => { setFile(event.target.files?.[0] ?? null); setReport(null); setDataLoss(null); }} />
						</label>
						<button type="button" onClick={validate} disabled={!file || !canWrite || working != null} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">
							{working === "validate" ? <LoadingSpinner size="sm" /> : <FiShield />} Valider sans publier
						</button>
						{!canWrite && <p className="mt-3 text-xs text-amber-700">Votre rôle dispose d'un accès en lecture seule.</p>}
					</div>

					<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
						<div className="flex items-start justify-between gap-3">
							<div>
								<h2 className="text-lg font-bold text-slate-900">2. Confirmer et publier l'aperçu</h2>
								<p className="mt-1 text-sm text-slate-500">Une confirmation remplace les 14 sujets publics qui se chevauchent avec l'Excel.</p>
							</div>
							<FiDatabase className="h-6 w-6 text-blue-700" />
						</div>
						<div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
							<div className="flex gap-2"><FiAlertTriangle className="mt-0.5 shrink-0" /><p>Le classeur devient la source de vérité. Les anciens contenus périodiques restent archivés pour retour arrière, mais ne servent plus le portail public.</p></div>
						</div>
						{pendingRemoval && (
							<DataRemovalConfirm
								ref={removalRef}
								items={pendingRemoval.items}
								listChanged={pendingRemoval.listChanged}
								confirmable={removalConfirmable}
								accepted={removalAccepted}
								onAcceptedChange={checked => setAcceptedRemovalKey(checked ? pendingRemovalKey : null)}
								disabled={!canWrite || working != null}
							/>
						)}
						<div className="mt-4 flex flex-wrap gap-2">
							{pendingRemoval ? (
								<button
									type="button"
									onClick={() => commit(true, pendingRemoval.fingerprint)}
									disabled={!report?.isValid || report.alreadyCommitted || !canWrite || working != null || !removalAccepted || !removalConfirmable}
									className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-50"
								>
									{working === "commit" ? <LoadingSpinner size="sm" /> : <FiAlertTriangle />} Confirmer le retrait de ces données
								</button>
							) : (
								<button type="button" onClick={() => commit(false)} disabled={!report?.isValid || report.alreadyCommitted || !canWrite || working != null} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">
									{working === "commit" ? <LoadingSpinner size="sm" /> : <FiCheck />} Confirmer le remplacement
								</button>
							)}
							<button type="button" onClick={republish} disabled={!latestCommitted || !canWrite || working != null} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
								<FiRefreshCw className={working === "publish" ? "animate-spin" : ""} /> Republier l'aperçu
							</button>
						</div>
						{/* Le geste QUOTIDIEN : sans retour visuel, 2-3 minutes de spinner muet
						    ressemblent à un blocage. Durée calibrée sur la mesure réelle du
						    02/08 en déployé (validation 4 s + commit 142 s ≈ 3 min). */}
						<EstimatedProgressBar
							active={working === "commit"}
							estimatedSeconds={180}
							label="Synchronisation du classeur : remplacement des observations puis publication de l'aperçu"
						/>
						<EstimatedProgressBar
							active={working === "publish"}
							estimatedSeconds={90}
							label="Republication des artefacts d'aperçu"
						/>
					</div>
				</section>

				{/* 3. État du portail : le compte rendu de ce que le classeur a produit, donc
				    sous les gestes de téléversement. */}
				{catalog && (
					<section className="space-y-3">
						<div className="flex items-center gap-2">
							<FiGrid className="text-blue-700" />
							<h2 className="text-lg font-bold text-slate-900">État du portail des données</h2>
							{catalog.activeBatch?.sourceAsOfUtc && (
								<span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
									Source au {shortDate(catalog.activeBatch.sourceAsOfUtc)}
								</span>
							)}
						</div>
						<CatalogTable totals={catalog.totals} datasets={catalog.datasets} />
					</section>
				)}

				<section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
					<div className="flex items-center gap-2"><FiClock className="text-blue-700" /><h2 className="text-lg font-bold text-slate-900">Historique immuable des imports</h2></div>
					{loading ? <div className="py-10"><LoadingSpinner /></div> : (
						<div className="mt-4 overflow-x-auto">
							<table className="min-w-full text-left text-sm">
								<thead>
									<tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
										<th className="px-3 py-3">Fichier</th>
										<th className="px-3 py-3">Statut</th>
										<th className="px-3 py-3">Source au</th>
										<th className="px-3 py-3">Créé</th>
										<th className="px-3 py-3">Volume</th>
										<th className="px-3 py-3">Classeur</th>
									</tr>
								</thead>
								<tbody>
									{history.map(item => (
										<tr key={item.id} className="border-b border-slate-100">
											<td className="px-3 py-3"><p className="font-semibold text-slate-800">{item.fileName}</p><p className="font-mono text-[11px] text-slate-400">{item.sha256.slice(0, 16)}…</p></td>
											<td className="px-3 py-3"><span className={`rounded-full px-2 py-1 text-xs font-bold ${item.status === "committed-publish-failed" ? "bg-amber-100 text-amber-800" : item.status.startsWith("committed") ? "bg-emerald-100 text-emerald-800" : item.status === "validated" ? "bg-blue-100 text-blue-800" : item.status === "superseded" ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-800"}`}>{item.status}</span>{item.report?.dataRemoval && (
												<span
													className="ml-1.5 inline-block rounded-full bg-rose-100 px-2 py-1 text-xs font-bold text-rose-800"
													title={[
														`Retrait confirmé${item.report.dataRemoval.acceptedBy ? ` par ${item.report.dataRemoval.acceptedBy}` : ""}`,
														...item.report.dataRemoval.items
													].join("\n")}
												>
													Retrait confirmé
												</span>
											)}</td>
											<td className="px-3 py-3">{shortDate(item.sourceAsOfUtc)}</td>
											<td className="px-3 py-3">{new Date(item.createdAtUtc).toLocaleString("fr-BE")}</td>
											<td className="px-3 py-3">{item.report ? `${number(item.report.seriesCount)} séries · ${number(item.report.observationCount)} obs.` : "-"}</td>
											<td className="px-3 py-3">
												{item.hasWorkbook ? (
													<button
														type="button"
														onClick={() => download(item)}
														disabled={downloading === item.id}
														className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
														title="Télécharger le classeur source exact de cet import"
													>
														{downloading === item.id ? <LoadingSpinner size="sm" /> : <FiDownload className="h-3.5 w-3.5" />} Télécharger
													</button>
												) : (
													<span className="text-xs text-slate-400" title="Blob purgé par la rétention ; métadonnées et rapport conservés.">Purgé (rétention)</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
							{history.length === 0 && <p className="py-8 text-center text-sm text-slate-500">Aucun import.</p>}
						</div>
					)}
				</section>
			</div>
		</div>
	);
};

export default MacroData;
